import { cssVariables } from "@zplanem/theme"
import type { Locale } from "@zplanem/translations"
import type React from "react"
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react"
import { getStoredLocale, setStoredLocale } from "./i18n"

interface LocaleContextType {
	locale: Locale
	setLocale: (locale: Locale) => void
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined)

export const LocaleProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const [locale, setLocaleState] = useState<Locale>(getStoredLocale() as Locale)

	const setLocale = useCallback((newLocale: Locale) => {
		setLocaleState(newLocale)
		setStoredLocale(newLocale)
	}, [])

	useEffect(() => {
		const root = document.documentElement
		Object.entries(cssVariables).forEach(([key, value]) => {
			root.style.setProperty(key, value)
		})
	}, [])

	return (
		<LocaleContext.Provider value={{ locale, setLocale }}>
			{children}
		</LocaleContext.Provider>
	)
}

export const useLocale = () => {
	const context = useContext(LocaleContext)
	if (context === undefined) {
		throw new Error("useLocale must be used within a LocaleProvider")
	}
	return context
}
